import { BUTTON_LABELS, CARD_LABELS, FEATURE_FLAGS, SCORE_LABELS, WORDS } from '@/constants';
import { capitalize } from 'lodash';
import { Card, ICardAction } from '@/components/Cards';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/Icons';
import { ScoreCircle } from '@/components/Score';
import { ScorePassFailCardProps } from './types';
import React, { useMemo } from 'react';

/**
 * Component to display a pass/fail card for simulation scores. This component is used in
 * both certification and practice simulations to show the user's score and provide appropriate
 * actions based on whether they passed or failed. The card displays different titles, labels, and
 * actions depending on the simulation type and card type (pass or fail).
 */
export const ScorePassFailCard: React.FC<ScorePassFailCardProps> = ({
  cardType,
  isLoading,
  onCertificationPracticeClick,
  onViewCertificateClick,
  score,
  showRetakeRolePlayButton,
  simulationType,
  subtitleComponent,
  subtitleComponentIsVisible = false,
}) => {
  /**
   * Determine the card title based on the simulation type. This ensures that we have the
   * correct title for both certification and practice simulations, and allows us to easily
   * extend this in the future if we add more simulation types. We use useMemo here to avoid
   * unnecessary recalculations of the title on every render, since it only depends on the
   * simulationType prop.
   */
  const cardTitle = useMemo(() => {
    if (simulationType === 'certification') {
      return CARD_LABELS.CERTIFICATION_SCORE;
    } else if (simulationType === 'practice') {
      return CARD_LABELS.PRACTICE_CERTIFICATION_SCORE;
    }

    return '';
  }, [simulationType]);

  /**
   * Determine the appropriate label based on the context (Manager vs. User),
   * card type (pass vs. fail), and simulation type (certification vs. practice).
   */
  const contextualLabel = useMemo(() => {
    // Helper function to get the correct label based on context and card type
    const getCertificationLabel = () => {
      return cardType === 'pass'
        ? SCORE_LABELS.USER_CERTIFICATION_PASS
        : SCORE_LABELS.USER_CERTIFICATION_FAIL;
    };

    // Helper function to get the correct practice label based on context and card type
    const getPracticeLabel = () => {
      return cardType === 'pass'
        ? SCORE_LABELS.USER_PRACTICE_PASS
        : SCORE_LABELS.USER_PRACTICE_FAIL;
    };

    // Determine label based on simulation type and return the appropriate one
    switch (simulationType) {
      case 'certification':
        return getCertificationLabel();
      case 'practice':
        return getPracticeLabel();
      default:
        return '';
    }
  }, [cardType, simulationType]);

  // ==============================================
  // Fail Card
  // ==============================================
  if (cardType === 'fail') {
    const failCardAction: ICardAction = {
      className: cn('no-print', showRetakeRolePlayButton ? '' : 'hidden'),
      iconRight: 'ArrowRight',
      label: BUTTON_LABELS.TRY_AGAIN,
      loading: isLoading,
      onClick: onCertificationPracticeClick,
      size: 'small',
      type: 'button',
      variant: 'primary',
    };

    return (
      <Card
        actions={[failCardAction]}
        contentClassName="gap-0"
        headerClassName="gap-2"
        title={FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? undefined : cardTitle}
        withBorder={false}
      >
        <div className="mb-2 flex w-full items-center justify-start gap-2 text-brand-primary">
          {FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? (
            <>
              <ScoreCircle
                className="mb-4"
                isLoading={isLoading}
                simulationType={simulationType}
                value={score || 0}
              />
            </>
          ) : (
            <>
              <div className="flex h-16 w-16 items-center justify-center">
                <Icon name="XCircle" size={64} />
              </div>
              <div className="typography-page-title font-bold">{capitalize(WORDS.FAIL)}</div>
            </>
          )}
        </div>
        {subtitleComponentIsVisible && subtitleComponent}
        <p
          className={cn('typography-body text-neutral-black', {
            'mb-4': showRetakeRolePlayButton,
          })}
        >
          {contextualLabel}
        </p>
      </Card>
    );
  }

  // ==============================================
  // Pass Card
  // ==============================================
  if (cardType === 'pass') {
    const passCardAction: ICardAction = {
      label: BUTTON_LABELS.VIEW_CERTIFICATE,
      loading: isLoading,
      onClick: onViewCertificateClick,
      size: 'small',
      type: 'button',
      variant: 'primary',
    };

    return (
      <Card
        actions={simulationType === 'certification' ? [passCardAction] : []}
        contentClassName="gap-0"
        headerClassName="gap-2"
        title={FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? undefined : cardTitle}
        withBorder={false}
      >
        <div className="mb-2 flex w-full items-center justify-start gap-2 text-brand-primary">
          {FEATURE_FLAGS.ENABLE_SCORECARD_V2 ? (
            <>
              <ScoreCircle
                className="mb-4"
                isLoading={isLoading}
                simulationType={simulationType}
                value={score || 0}
              />
            </>
          ) : (
            <>
              <div className="flex h-16 w-16 items-center justify-center">
                <Icon name="CheckCircle" size={64} />
              </div>
              <div className="typography-page-title font-bold">{capitalize(WORDS.PASS)}</div>
            </>
          )}
        </div>
        {subtitleComponentIsVisible && subtitleComponent}
        <p
          className={cn('typography-body text-neutral-black', {
            'mb-4': simulationType === 'certification',
          })}
        >
          {contextualLabel}
        </p>
      </Card>
    );
  }

  return null;
};

ScorePassFailCard.displayName = 'ScorePassFailCard';

export default ScorePassFailCard;
